import React from 'react';

interface IconProps {
  className?: string;
  size?: number;
}

// Target icon - for "All" and "Practice Scenarios"
export const TargetIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
  </svg>
);

// Book icon - for "Basic Strategy"
export const BookIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    <path d="M8 7h8" />
    <path d="M8 11h6" />
  </svg>
);

// Playing Card icon - for "Soft Hands"
export const CardIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect x="3" y="2" width="14" height="20" rx="2" />
    <path d="M10 8L7 12l3 4" />
    <path d="M17 6v12" />
    <path d="M21 6v12" />
    <circle cx="10" cy="12" r="1" fill="currentColor" />
  </svg>
);

// Ace of Spades style card - alternative for "Soft Hands"
export const AceIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect x="4" y="2" width="16" height="20" rx="2" />
    <path d="M12 6v4" />
    <path d="M9 14c0-2 1.5-4 3-4s3 2 3 4c0 1.5-1 2.5-2 3h-2c-1-.5-2-1.5-2-3z" fill="currentColor" />
    <circle cx="12" cy="18" r="1" fill="currentColor" />
  </svg>
);

// Scissors icon - for "Splits"
export const ScissorsIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="6" cy="6" r="3" />
    <circle cx="6" cy="18" r="3" />
    <line x1="20" y1="4" x2="8.12" y2="15.88" />
    <line x1="14.47" y1="14.48" x2="20" y2="20" />
    <line x1="8.12" y1="8.12" x2="12" y2="12" />
  </svg>
);

// Graduation Cap icon - for "Advanced"
export const GraduationCapIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
    <path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5" />
  </svg>
);

// Chart/Bar icon - for "Deviations" and stats
export const ChartIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
    <line x1="3" y1="20" x2="21" y2="20" />
  </svg>
);

// Shield icon - for "Insurance"
export const ShieldIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

// Shield with X - for declining insurance
export const ShieldXIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <line x1="9" y1="9" x2="15" y2="15" />
    <line x1="15" y1="9" x2="9" y2="15" />
  </svg>
);

// Crosshair/Bullseye - alternative for precision
export const CrosshairIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="22" y1="12" x2="18" y2="12" />
    <line x1="6" y1="12" x2="2" y2="12" />
    <line x1="12" y1="6" x2="12" y2="2" />
    <line x1="12" y1="22" x2="12" y2="18" />
    <circle cx="12" cy="12" r="2" fill="currentColor" />
  </svg>
);

// Check circle icon
export const CheckCircleIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

// X circle icon
export const XCircleIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="15" y1="9" x2="9" y2="15" />
    <line x1="9" y1="9" x2="15" y2="15" />
  </svg>
);

// Cards stack icon - playing cards
export const CardsStackIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 463.644 463.644"
    fill="currentColor"
    className={className}
  >
    <path d="M463.164,146.031l-77.369,288.746c-1.677,6.26-7.362,10.4-13.556,10.401c-1.198,0-2.414-0.155-3.625-0.479l-189.261-50.712c-7.472-2.003-11.922-9.711-9.919-17.183l2.041-7.616c1.287-4.801,6.222-7.647,11.023-6.363c4.801,1.287,7.65,6.222,6.363,11.023l-1.013,3.78l181.587,48.656l75.314-281.076l-77.031-20.64c-4.801-1.287-7.651-6.222-6.364-11.023s6.225-7.648,11.022-6.364l80.869,21.668C460.718,130.853,465.167,138.56,463.164,146.031z M166.128,56.029c-4.971,0-9,4.029-9,9v8.565c0,4.971,4.029,9,9,9s9-4.029,9-9v-8.565C175.128,60.058,171.099,56.029,166.128,56.029z M280.889,176.762c2.202,3.114,2.202,7.278,0,10.393l-41.716,58.996c-1.687,2.385-4.427,3.804-7.349,3.804c-2.921,0-5.662-1.418-7.348-3.804l-41.718-58.996c-2.202-3.114-2.202-7.278,0-10.393l41.718-58.996c1.687-2.385,4.427-3.804,7.348-3.804c2.922,0,5.662,1.418,7.349,3.804L280.889,176.762z M262.518,181.958l-30.694-43.408l-30.694,43.408l30.694,43.407L262.518,181.958z M343.016,380.764l-2.216,8.273c-1.286,4.801,1.563,9.736,6.365,11.022c0.78,0.209,1.563,0.309,2.334,0.309c3.974,0,7.611-2.653,8.688-6.674l2.216-8.273c1.286-4.801-1.563-9.736-6.365-11.022C349.237,373.111,344.302,375.963,343.016,380.764z M112.375,215.913c2.577-0.69,5.056-1.089,7.454-1.195V32.492c0-7.736,6.293-14.029,14.028-14.029h195.935c7.736,0,14.03,6.293,14.03,14.029v182.225c2.396,0.106,4.875,0.505,7.45,1.195c16.511,4.424,26.346,21.457,21.922,37.968c-4.28,15.974-17.951,28.108-29.372,36.404v41.139c0,7.736-6.294,14.03-14.03,14.03H133.857c-7.735,0-14.028-6.294-14.028-14.03v-41.137c-11.422-8.295-25.093-20.428-29.376-36.405c-2.143-7.996-1.042-16.35,3.1-23.523C97.695,223.186,104.38,218.055,112.375,215.913z M343.821,267.05c6.531-6.172,10.424-12,11.985-17.828c1.855-6.924-2.27-14.067-9.194-15.923c-1.047-0.281-1.97-0.451-2.791-0.538V267.05z M137.829,327.454h187.992v-41.7c-0.001-0.08-0.001-0.161,0-0.241v-59.907c-0.003-0.13-0.003-0.261,0-0.391V36.463H137.829v188.755c0.003,0.13,0.003,0.261,0,0.392v59.898c0.001,0.084,0.001,0.168,0,0.252V327.454z M107.84,249.222c1.563,5.83,5.457,11.66,11.989,17.832v-34.292c-0.822,0.086-1.746,0.256-2.794,0.537c-3.353,0.898-6.156,3.051-7.894,6.061C107.404,242.369,106.942,245.871,107.84,249.222z M173.576,405.019l-79.363,21.265L18.897,145.209l77.031-20.641c4.801-1.287,7.651-6.222,6.364-11.023c-1.287-4.801-6.225-7.65-11.022-6.364L10.402,128.85c-3.614,0.968-6.637,3.29-8.512,6.538c-1.876,3.249-2.376,7.029-1.407,10.644l77.37,288.743c0.968,3.616,3.29,6.641,6.54,8.518c2.166,1.25,4.567,1.89,7,1.89c1.216,0,2.439-0.16,3.644-0.482l83.199-22.293c4.801-1.287,7.651-6.222,6.364-11.022C183.312,406.581,178.377,403.734,173.576,405.019z M51.298,156.782c-4.801,1.287-7.65,6.222-6.364,11.023l2.217,8.274c1.078,4.021,4.714,6.673,8.688,6.673c0.771,0,1.555-0.1,2.335-0.309c4.801-1.287,7.65-6.222,6.364-11.023l-2.217-8.274C61.034,158.344,56.101,155.496,51.298,156.782z M297.52,281.322c-4.971,0-9,4.029-9,9v8.565c0,4.971,4.029,9,9,9s9-4.029,9-9v-8.565C306.52,285.352,302.491,281.322,297.52,281.322z"/>
  </svg>
);

// Trend up icon - for deviations
export const TrendUpIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

// Hand trainer / pointing icon
export const HandPointerIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M18 11V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0" />
    <path d="M14 10V4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v2" />
    <path d="M10 10.5V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v8" />
    <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
  </svg>
);

// Split/Fork icon - alternative for splits
export const SplitIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M16 3h5v5" />
    <path d="M8 3H3v5" />
    <path d="M12 22v-8.3a4 4 0 0 0-1.172-2.872L3 3" />
    <path d="m15 9 6-6" />
  </svg>
);

// Award/Star icon - for achievements
export const AwardIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="12" cy="8" r="6" />
    <path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11" />
  </svg>
);

// Coins/Betting icon
export const CoinsIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="8" cy="8" r="6" />
    <path d="M18.09 10.37A6 6 0 1 1 10.34 18" />
    <path d="M7 6h2v4" />
    <path d="M16 14h2v4" />
  </svg>
);

// Dice icon - for table simulator
export const DiceIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect x="2" y="2" width="20" height="20" rx="3" />
    <circle cx="8" cy="8" r="1.5" fill="currentColor" />
    <circle cx="16" cy="8" r="1.5" fill="currentColor" />
    <circle cx="8" cy="16" r="1.5" fill="currentColor" />
    <circle cx="16" cy="16" r="1.5" fill="currentColor" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" />
  </svg>
);

// Lightning bolt icon - for speed drill
export const LightningIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

// Slot machine / Game icon - for edge calculator
export const SlotMachineIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <line x1="9" y1="3" x2="9" y2="21" />
    <line x1="15" y1="3" x2="15" y2="21" />
    <rect x="4" y="8" width="4" height="8" rx="1" fill="currentColor" fillOpacity="0.3" />
    <rect x="10" y="8" width="4" height="8" rx="1" fill="currentColor" fillOpacity="0.3" />
    <rect x="16" y="8" width="4" height="8" rx="1" fill="currentColor" fillOpacity="0.3" />
  </svg>
);

// Line chart / Trend icon - for simulator
export const LineChartIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <line x1="3" y1="20" x2="21" y2="20" />
    <line x1="3" y1="20" x2="3" y2="4" />
    <polyline points="6 17 10 11 14 14 20 6" />
    <polyline points="16 6 20 6 20 10" />
  </svg>
);

// Dollar / Money icon - for financial
export const DollarIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

// Warning icon
export const WarningIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

// Check icon (simple)
export const CheckIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// X icon (simple)
export const XIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// Spade suit icon - for blackjack app header
export const SpadeIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
  >
    <path d="M12 2C12 2 4 10 4 14c0 2.5 2 4.5 4.5 4.5 1.5 0 2.5-.5 3.5-1.5v4h-2v1h4v-1h-2v-4c1 1 2 1.5 3.5 1.5 2.5 0 4.5-2 4.5-4.5C20 10 12 2 12 2z"/>
  </svg>
);

// Hand pointing up icon - for Hit action
export const HandUpIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M14 4.1 12 6" />
    <path d="m5.1 8-2.9-.8" />
    <path d="m6 12-1.9 2" />
    <path d="M7.2 2.2 8 5.1" />
    <circle cx="12" cy="13" r="3" />
    <path d="M12 10V2" />
    <path d="m15 8.2 1.8-2.4" />
  </svg>
);

// Hand stop icon - for Stand action  
export const HandStopIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M18 11V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0" />
    <path d="M14 10V4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v2" />
    <path d="M10 10.5V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v8" />
    <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
  </svg>
);

// Double down icon (two arrows up)
export const DoubleDownIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M8 18V6" />
    <path d="m4 10 4-4 4 4" />
    <path d="M16 18V6" />
    <path d="m12 10 4-4 4 4" />
  </svg>
);

// Flag icon - for Surrender
export const FlagIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
    <line x1="4" y1="22" x2="4" y2="15" />
  </svg>
);

// Checkered flag icon - for completion/finish
export const CheckeredFlagIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    className={className}
  >
    {/* Flag pole */}
    <line x1="4" y1="2" x2="4" y2="22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    {/* Flag shape */}
    <path d="M4 3h16v12H4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    {/* Checkered pattern - row 1 */}
    <rect x="4" y="3" width="4" height="4" fill="currentColor" />
    <rect x="12" y="3" width="4" height="4" fill="currentColor" />
    {/* Checkered pattern - row 2 */}
    <rect x="8" y="7" width="4" height="4" fill="currentColor" />
    <rect x="16" y="7" width="4" height="4" fill="currentColor" />
    {/* Checkered pattern - row 3 */}
    <rect x="4" y="11" width="4" height="4" fill="currentColor" />
    <rect x="12" y="11" width="4" height="4" fill="currentColor" />
  </svg>
);

// Wallet X / Bankrupt icon
export const WalletXIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
    <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
    <path d="M18 12a2 2 0 0 0 0 4h4v-4Z" />
    <line x1="2" y1="2" x2="22" y2="22" strokeWidth="2.5" />
  </svg>
);

// Shuffle icon - for advanced stats
export const ShuffleIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polyline points="16 3 21 3 21 8" />
    <line x1="4" y1="20" x2="21" y2="3" />
    <polyline points="21 16 21 21 16 21" />
    <line x1="15" y1="15" x2="21" y2="21" />
    <line x1="4" y1="4" x2="9" y2="9" />
  </svg>
);

// Brain icon - for count tracking
export const BrainIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 463 463"
    fill="currentColor"
    className={className}
  >
    <path d="M151.245,222.446C148.054,237.039,135.036,248,119.5,248c-4.142,0-7.5,3.357-7.5,7.5s3.358,7.5,7.5,7.5c23.774,0,43.522-17.557,46.966-40.386c14.556-1.574,27.993-8.06,38.395-18.677c2.899-2.959,2.85-7.708-0.109-10.606c-2.958-2.897-7.707-2.851-10.606,0.108C184.947,202.829,172.643,208,159.5,208c-26.743,0-48.5-21.757-48.5-48.5c0-4.143-3.358-7.5-7.5-7.5s-7.5,3.357-7.5,7.5C96,191.715,120.119,218.384,151.245,222.446z"/>
    <path d="M183,287.5c0-4.143-3.358-7.5-7.5-7.5c-35.014,0-63.5,28.486-63.5,63.5c0,0.362,0.013,0.725,0.019,1.088C109.23,344.212,106.39,344,103.5,344c-4.142,0-7.5,3.357-7.5,7.5s3.358,7.5,7.5,7.5c26.743,0,48.5,21.757,48.5,48.5c0,4.143,3.358,7.5,7.5,7.5s7.5-3.357,7.5-7.5c0-26.611-16.462-49.437-39.731-58.867c-0.178-1.699-0.269-3.418-0.269-5.133c0-26.743,21.757-48.5,48.5-48.5C179.642,295,183,291.643,183,287.5z"/>
    <path d="M439,223.5c0-17.075-6.82-33.256-18.875-45.156c1.909-6.108,2.875-12.426,2.875-18.844c0-30.874-22.152-56.659-51.394-62.329C373.841,91.6,375,85.628,375,79.5c0-19.557-11.883-36.387-28.806-43.661C317.999,13.383,287.162,0,263.5,0c-13.153,0-24.817,6.468-32,16.384C224.317,6.468,212.653,0,199.5,0c-23.662,0-54.499,13.383-82.694,35.839C99.883,43.113,88,59.943,88,79.5c0,6.128,1.159,12.1,3.394,17.671C62.152,102.841,40,128.626,40,159.5c0,6.418,0.965,12.735,2.875,18.844C30.82,190.244,24,206.425,24,223.5c0,13.348,4.149,25.741,11.213,35.975C27.872,270.087,24,282.466,24,295.5c0,23.088,12.587,44.242,32.516,55.396C56.173,353.748,56,356.626,56,359.5c0,31.144,20.315,58.679,49.79,68.063C118.611,449.505,141.965,463,167.5,463c27.995,0,52.269-16.181,64-39.674c11.731,23.493,36.005,39.674,64,39.674c25.535,0,48.889-13.495,61.71-35.437c29.475-9.385,49.79-36.92,49.79-68.063c0-2.874-0.173-5.752-0.516-8.604C426.413,339.742,439,318.588,439,295.5c0-13.034-3.872-25.413-11.213-36.025C434.851,249.241,439,236.848,439,223.5z M167.5,448c-21.029,0-40.191-11.594-50.009-30.256c-0.973-1.849-2.671-3.208-4.688-3.751C88.19,407.369,71,384.961,71,359.5c0-3.81,0.384-7.626,1.141-11.344c0.702-3.447-1.087-6.92-4.302-8.35C50.32,332.018,39,314.626,39,295.5c0-8.699,2.256-17.014,6.561-24.379C56.757,280.992,71.436,287,87.5,287c4.142,0,7.5-3.357,7.5-7.5s-3.358-7.5-7.5-7.5C60.757,272,39,250.243,39,223.5c0-14.396,6.352-27.964,17.428-37.221c2.5-2.09,3.365-5.555,2.14-8.574C56.2,171.869,55,165.744,55,159.5c0-26.743,21.757-48.5,48.5-48.5s48.5,21.757,48.5,48.5c0,4.143,3.358,7.5,7.5,7.5s7.5-3.357,7.5-7.5c0-33.642-26.302-61.243-59.421-63.355C104.577,91.127,103,85.421,103,79.5c0-13.369,8.116-24.875,19.678-29.859c0.447-0.133,0.885-0.307,1.308-0.527C127.568,47.752,131.447,47,135.5,47c12.557,0,23.767,7.021,29.256,18.325c1.81,3.727,6.298,5.281,10.023,3.47c3.726-1.809,5.28-6.296,3.47-10.022c-6.266-12.903-18.125-22.177-31.782-25.462C165.609,21.631,184.454,15,199.5,15c13.509,0,24.5,10.99,24.5,24.5v97.051c-6.739-5.346-15.25-8.551-24.5-8.551c-4.142,0-7.5,3.357-7.5,7.5s3.358,7.5,7.5,7.5c13.509,0,24.5,10.99,24.5,24.5v180.279c-9.325-12.031-22.471-21.111-37.935-25.266c-3.999-1.071-8.114,1.297-9.189,5.297c-1.075,4.001,1.297,8.115,5.297,9.189C206.8,343.616,224,366.027,224,391.5C224,422.654,198.654,448,167.5,448z M395.161,339.807c-3.215,1.43-5.004,4.902-4.302,8.35c0.757,3.718,1.141,7.534,1.141,11.344c0,25.461-17.19,47.869-41.803,54.493c-2.017,0.543-3.716,1.902-4.688,3.751C335.691,436.406,316.529,448,295.5,448c-31.154,0-56.5-25.346-56.5-56.5c0-2.109-0.098-4.2-0.281-6.271c0.178-0.641,0.281-1.314,0.281-2.012V135.5c0-13.51,10.991-24.5,24.5-24.5c4.142,0,7.5-3.357,7.5-7.5s-3.358-7.5-7.5-7.5c-9.25,0-17.761,3.205-24.5,8.551V39.5c0-13.51,10.991-24.5,24.5-24.5c15.046,0,33.891,6.631,53.033,18.311c-13.657,3.284-25.516,12.559-31.782,25.462c-1.81,3.727-0.256,8.214,3.47,10.022c3.726,1.81,8.213,0.257,10.023-3.47C303.733,54.021,314.943,47,327.5,47c4.053,0,7.933,0.752,11.514,2.114c0.422,0.22,0.86,0.393,1.305,0.526C351.883,54.624,360,66.13,360,79.5c0,5.921-1.577,11.627-4.579,16.645C322.302,98.257,296,125.858,296,159.5c0,4.143,3.358,7.5,7.5,7.5s7.5-3.357,7.5-7.5c0-26.743,21.757-48.5,48.5-48.5s48.5,21.757,48.5,48.5c0,6.244-1.2,12.369-3.567,18.205c-1.225,3.02-0.36,6.484,2.14,8.574C417.648,195.536,424,209.104,424,223.5c0,26.743-21.757,48.5-48.5,48.5c-4.142,0-7.5,3.357-7.5,7.5s3.358,7.5,7.5,7.5c16.064,0,30.743-6.008,41.939-15.879c4.306,7.365,6.561,15.68,6.561,24.379C424,314.626,412.68,332.018,395.161,339.807z"/>
    <path d="M359.5,240c-15.536,0-28.554-10.961-31.745-25.554C358.881,210.384,383,183.715,383,151.5c0-4.143-3.358-7.5-7.5-7.5s-7.5,3.357-7.5,7.5c0,26.743-21.757,48.5-48.5,48.5c-13.143,0-25.447-5.171-34.646-14.561c-2.898-2.958-7.647-3.007-10.606-0.108s-3.008,7.647-0.109,10.606c10.402,10.617,23.839,17.103,38.395,18.677C315.978,237.443,335.726,255,359.5,255c4.142,0,7.5-3.357,7.5-7.5S363.642,240,359.5,240z"/>
    <path d="M335.5,328c-2.89,0-5.73,0.212-8.519,0.588c0.006-0.363,0.019-0.726,0.019-1.088c0-35.014-28.486-63.5-63.5-63.5c-4.142,0-7.5,3.357-7.5,7.5s3.358,7.5,7.5,7.5c26.743,0,48.5,21.757,48.5,48.5c0,1.714-0.091,3.434-0.269,5.133C288.462,342.063,272,364.889,272,391.5c0,4.143,3.358,7.5,7.5,7.5s7.5-3.357,7.5-7.5c0-26.743,21.757-48.5,48.5-48.5c4.142,0,7.5-3.357,7.5-7.5S339.642,328,335.5,328z"/>
  </svg>
);

// Playing cards deck icon
export const DeckIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect x="4" y="4" width="14" height="18" rx="2" />
    <rect x="6" y="2" width="14" height="18" rx="2" fill="none" />
    <path d="M11 10v4" />
    <path d="M9 12h4" />
  </svg>
);

// Counting icon - abacus style
export const CountingIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    {/* Left and right vertical posts */}
    <line x1="4" y1="2" x2="4" y2="22" strokeWidth="2" />
    <line x1="20" y1="2" x2="20" y2="22" strokeWidth="2" />
    {/* Horizontal rods */}
    <line x1="4" y1="5" x2="20" y2="5" />
    <line x1="4" y1="10" x2="20" y2="10" />
    <line x1="4" y1="15" x2="20" y2="15" />
    <line x1="4" y1="20" x2="20" y2="20" />
    {/* Beads - row 1 (1 bead) */}
    <circle cx="7" cy="5" r="1.5" fill="currentColor" />
    {/* Beads - row 2 (2 beads) */}
    <circle cx="8" cy="10" r="1.5" fill="currentColor" />
    <circle cx="11" cy="10" r="1.5" fill="currentColor" />
    {/* Beads - row 3 (3 beads) */}
    <circle cx="9" cy="15" r="1.5" fill="currentColor" />
    <circle cx="12" cy="15" r="1.5" fill="currentColor" />
    <circle cx="15" cy="15" r="1.5" fill="currentColor" />
    {/* Beads - row 4 (4 beads) */}
    <circle cx="8" cy="20" r="1.5" fill="currentColor" />
    <circle cx="11" cy="20" r="1.5" fill="currentColor" />
    <circle cx="14" cy="20" r="1.5" fill="currentColor" />
    <circle cx="17" cy="20" r="1.5" fill="currentColor" />
  </svg>
);

// Betting icon - chips stacked
export const BettingIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 33 33"
    fill="currentColor"
    className={className}
  >
    <path d="M18.186,27.506 L18.22,27.468 C19.147,28.279 20.305,28.833 21.582,29.019 L21.879,29.056 L21.879,33 C19.518,32.854 17.37,31.928 15.687,30.476 L15.438,30.254 L18.186,27.506 L18.22,27.468 Z M27.032,27.302 L29.711,29.982 C29.745,30.017 29.783,30.047 29.824,30.072 C28.163,31.678 25.973,32.741 23.54,32.973 L23.191,33 L23.191,29.056 C24.672,28.909 26.007,28.268 27.032,27.302 Z M0.077,25.145 C1.868,27.316 5.469,28.69 9.555,28.69 C10.236,28.69 10.903,28.652 11.551,28.578 C12.03,29.469 12.615,30.297 13.287,31.043 C12.138,31.325 10.875,31.48 9.555,31.48 C4.335,31.48 0.017,29.064 0,25.892 C-0.001,25.638 0.025,25.389 0.077,25.145 Z M15.981,23.19 L16.014,23.189 C16.127,24.33 16.533,25.386 17.156,26.279 L17.331,26.518 L17.259,26.579 L14.531,29.308 C13.189,27.725 12.309,25.738 12.098,23.556 L12.07,23.191 L15.981,23.19 L16.014,23.189 Z M29.056,23.189 L29.088,23.19 L33,23.191 C32.869,25.306 32.112,27.252 30.91,28.844 L30.693,29.122 L30.637,29.055 L27.892,26.31 C28.527,25.411 28.942,24.344 29.056,23.189 Z M22.535,17.292 C25.43,17.292 27.777,19.639 27.777,22.535 C27.777,25.43 25.43,27.777 22.535,27.777 C19.639,27.777 17.292,25.43 17.292,22.535 C17.292,19.639 19.639,17.292 22.535,17.292 Z M0.077,21.122 C1.868,23.292 5.469,24.667 9.555,24.667 C9.778,24.667 10,24.663 10.221,24.655 C10.374,25.607 10.636,26.525 10.992,27.393 C10.523,27.435 10.043,27.456 9.555,27.456 C4.335,27.456 0.017,25.04 0,21.869 C-0.001,21.615 0.025,21.365 0.077,21.122 Z M22.52,19.258 C22.292,19.258 22.103,19.423 22.063,19.639 L22.056,19.723 L22.056,20.017 L21.896,20.059 C21.172,20.275 20.733,20.803 20.733,21.476 C20.733,22.378 21.515,22.757 22.197,22.986 C22.755,23.174 22.972,23.344 22.972,23.591 C22.972,23.844 22.721,23.995 22.301,23.995 C22.018,23.995 21.729,23.943 21.441,23.841 C21.313,23.795 21.167,23.805 21.047,23.869 C20.95,23.92 20.874,24.003 20.83,24.102 L20.803,24.179 L20.787,24.24 L20.772,24.334 C20.752,24.583 20.906,24.821 21.152,24.9 C21.351,24.964 21.572,25.011 21.794,25.039 L22.016,25.061 L22.016,25.345 L22.024,25.428 C22.063,25.645 22.253,25.809 22.481,25.809 C22.709,25.809 22.898,25.645 22.938,25.428 L22.945,25.345 L22.945,24.997 L23.101,24.96 C23.86,24.75 24.339,24.188 24.339,23.481 C24.339,22.738 23.932,22.277 22.979,21.941 C22.316,21.691 22.092,21.541 22.092,21.343 C22.092,21.113 22.302,20.986 22.685,20.986 C22.925,20.986 23.151,21.019 23.374,21.087 C23.504,21.127 23.648,21.112 23.767,21.046 C23.862,20.992 23.938,20.908 23.982,20.809 L24.008,20.732 L24.015,20.704 L24.031,20.609 C24.049,20.357 23.885,20.118 23.632,20.051 C23.502,20.017 23.362,19.99 23.214,19.972 L22.985,19.951 L22.985,19.723 L22.977,19.639 C22.938,19.423 22.748,19.258 22.52,19.258 Z M0.077,17.099 C1.868,19.269 5.469,20.644 9.555,20.644 C9.777,20.644 9.998,20.64 10.218,20.631 C10.112,21.282 10.058,21.95 10.058,22.63 C10.058,22.897 10.066,23.162 10.083,23.425 C9.908,23.43 9.732,23.433 9.555,23.433 C4.335,23.433 0.017,21.017 0,17.846 C-0.001,17.591 0.025,17.342 0.077,17.099 Z M14.531,15.761 L17.259,18.49 L17.331,18.551 C16.669,19.414 16.217,20.446 16.052,21.572 L16.014,21.881 L15.981,21.879 L12.07,21.879 C12.213,19.553 13.115,17.432 14.531,15.761 Z M27.63,18.413 L30.42,15.623 C31.826,17.226 32.751,19.261 32.97,21.503 L33,21.879 L29.088,21.879 L29.056,21.881 C28.936,20.674 28.49,19.564 27.806,18.64 L27.63,18.413 L30.42,15.623 Z M0.077,13.075 C1.868,15.246 5.469,16.62 9.555,16.62 C10.272,16.62 10.975,16.578 11.655,16.496 C11.149,17.396 10.753,18.365 10.481,19.384 C10.177,19.401 9.867,19.41 9.555,19.41 C4.335,19.41 0.017,16.994 0,13.822 C-0.001,13.568 0.025,13.319 0.077,13.075 Z M21.879,12.07 L21.879,15.981 C21.879,15.993 21.88,16.004 21.881,16.014 C20.585,16.142 19.4,16.648 18.439,17.419 L18.222,17.602 L18.186,17.564 L15.438,14.816 C17.155,13.236 19.4,12.223 21.879,12.07 Z M23.19,15.981 L23.191,12.07 C25.483,12.211 27.577,13.089 29.237,14.471 L29.495,14.693 L26.707,17.481 C25.807,16.737 24.704,16.228 23.493,16.051 L23.189,16.014 L23.19,15.981 L23.191,12.07 Z M0.077,8.753 C1.869,10.923 5.469,12.297 9.555,12.297 C13.641,12.297 17.241,10.923 19.033,8.753 C19.085,8.996 19.111,9.245 19.11,9.499 C19.108,9.883 19.043,10.257 18.92,10.616 C16.495,11.362 14.384,12.824 12.831,14.753 C11.807,14.97 10.703,15.087 9.555,15.087 C4.335,15.087 0.017,12.671 0,9.499 C-0.001,9.245 0.025,8.996 0.077,8.753 Z M9.331,11.179 L9.555,11.181 L9.394,11.181 L9.331,11.179 Z M9.555,0 C14.807,0 19.127,2.417 19.11,5.594 C19.106,6.26 18.913,6.892 18.562,7.476 L18.526,7.531 L18.455,7.644 L18.451,7.648 L18.453,7.644 L18.441,7.663 L18.378,7.757 C18.22,7.981 18.037,8.197 17.833,8.404 L17.766,8.47 C17.725,8.511 17.684,8.551 17.641,8.59 C17.625,8.604 17.609,8.619 17.593,8.633 C17.551,8.673 17.507,8.711 17.463,8.749 C17.446,8.763 17.428,8.778 17.411,8.793 C17.112,9.045 16.778,9.282 16.414,9.5 C16.38,9.52 16.345,9.541 16.309,9.562 L16.191,9.629 L16.098,9.681 C15.942,9.766 15.781,9.848 15.616,9.927 C15.598,9.936 15.579,9.944 15.561,9.953 C15.51,9.977 15.459,10.001 15.407,10.024 C15.353,10.048 15.299,10.072 15.244,10.095 C15.223,10.105 15.201,10.114 15.179,10.123 C15.124,10.146 15.069,10.169 15.013,10.192 C14.968,10.21 14.923,10.228 14.877,10.246 C14.848,10.257 14.819,10.268 14.79,10.279 L14.75,10.295 L14.649,10.332 C14.566,10.362 14.481,10.392 14.395,10.422 C14.337,10.441 14.279,10.461 14.221,10.48 C14.21,10.483 14.2,10.486 14.19,10.49 C14.104,10.517 14.018,10.544 13.931,10.57 C13.917,10.574 13.903,10.578 13.889,10.582 C13.807,10.607 13.724,10.63 13.641,10.653 C13.62,10.659 13.599,10.664 13.578,10.67 C13.288,10.748 12.991,10.817 12.687,10.878 L12.595,10.896 C12.518,10.911 12.442,10.925 12.365,10.939 C12.327,10.946 12.289,10.952 12.252,10.959 C12.198,10.968 12.145,10.977 12.091,10.985 L11.928,11.01 C11.877,11.018 11.825,11.025 11.773,11.032 L11.637,11.05 C11.46,11.073 11.28,11.093 11.098,11.11 C11.015,11.118 10.932,11.125 10.848,11.131 L10.677,11.144 C10.627,11.147 10.577,11.15 10.527,11.153 C10.481,11.156 10.435,11.158 10.389,11.161 C10.32,11.164 10.252,11.167 10.183,11.17 L10.058,11.174 C9.999,11.176 9.938,11.177 9.877,11.178 L9.716,11.181 L9.555,11.181 L9.278,11.178 L9.051,11.174 L8.928,11.17 C8.858,11.167 8.79,11.164 8.721,11.161 C8.676,11.158 8.63,11.156 8.585,11.153 C8.533,11.15 8.481,11.147 8.429,11.144 C8.375,11.14 8.322,11.136 8.269,11.132 C8.183,11.125 8.097,11.118 8.012,11.11 C7.83,11.093 7.65,11.073 7.472,11.05 C7.431,11.045 7.389,11.039 7.347,11.033 C7.288,11.025 7.229,11.017 7.171,11.008 C7.125,11.002 7.079,10.995 7.034,10.987 C7.004,10.983 6.974,10.978 6.943,10.973 L6.746,10.939 C6.668,10.925 6.592,10.911 6.515,10.896 L6.423,10.878 C6.119,10.817 5.822,10.748 5.532,10.67 C5.511,10.664 5.49,10.659 5.469,10.653 C5.386,10.631 5.303,10.607 5.221,10.582 L5.179,10.57 C5.092,10.544 5.006,10.517 4.92,10.49 C4.91,10.486 4.9,10.483 4.889,10.48 C4.831,10.461 4.773,10.441 4.715,10.422 C4.629,10.392 4.544,10.363 4.46,10.332 C4.427,10.32 4.394,10.307 4.36,10.295 L4.32,10.28 L4.233,10.246 C4.188,10.228 4.142,10.21 4.097,10.192 C4.041,10.17 3.986,10.147 3.931,10.123 C3.909,10.114 3.888,10.105 3.866,10.096 C3.811,10.072 3.757,10.048 3.703,10.024 L3.549,9.953 L3.494,9.927 C3.329,9.848 3.168,9.766 3.012,9.681 L2.919,9.629 C2.879,9.607 2.84,9.585 2.801,9.562 C2.76,9.538 2.719,9.514 2.679,9.49 C2.649,9.472 2.618,9.453 2.587,9.434 L2.522,9.393 L2.438,9.339 C2.247,9.215 2.065,9.085 1.893,8.951 C1.854,8.92 1.815,8.889 1.777,8.858 C1.759,8.843 1.741,8.829 1.724,8.814 C1.689,8.785 1.655,8.756 1.621,8.726 C1.588,8.698 1.555,8.669 1.523,8.64 L1.469,8.59 C1.426,8.551 1.385,8.511 1.344,8.47 L1.277,8.404 C1.179,8.305 1.086,8.203 0.999,8.1 C0.984,8.083 0.97,8.066 0.956,8.049 C0.922,8.008 0.888,7.966 0.856,7.924 L0.808,7.861 C0.787,7.833 0.766,7.805 0.746,7.777 C0.722,7.744 0.699,7.711 0.677,7.678 C0.67,7.668 0.664,7.658 0.657,7.648 L0.582,7.532 C0.209,6.932 0.004,6.28 0,5.594 C-0.017,2.417 4.303,0 9.555,0 Z M9.278,11.178 L9.291,11.179 L9.233,11.178 L9.278,11.178 Z"/>
  </svg>
);

// Trophy icon for achievements
export const TrophyIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
    <path d="M4 22h16" />
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
  </svg>
);

// Medal icon for milestones
export const MedalIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M7.21 15 2.66 7.14a2 2 0 0 1 .13-2.2L4.4 2.8A2 2 0 0 1 6 2h12a2 2 0 0 1 1.6.8l1.6 2.14a2 2 0 0 1 .14 2.2L16.79 15" />
    <path d="M11 12 5.12 2.2" />
    <path d="m13 12 5.88-9.8" />
    <path d="M8 7h8" />
    <circle cx="12" cy="17" r="5" />
    <path d="M12 18v-2h-.5" />
  </svg>
);

// Gamepad icon for gameplay
export const GamepadIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <line x1="6" x2="10" y1="12" y2="12" />
    <line x1="8" x2="8" y1="10" y2="14" />
    <line x1="15" x2="15.01" y1="13" y2="13" />
    <line x1="18" x2="18.01" y1="11" y2="11" />
    <rect width="20" height="12" x="2" y="6" rx="2" />
  </svg>
);

// Clock/Timer icon for session time
export const ClockIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

// Download icon for export buttons
export const DownloadIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

// Refresh/Reset icon
export const RefreshIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
    <path d="M3 21v-5h5" />
  </svg>
);

// History/Scroll icon for recent hands
export const HistoryIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
    <path d="M12 7v5l4 2" />
  </svg>
);

// Eye open icon
export const EyeIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

// Eye closed/hidden icon
export const EyeOffIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M3 3l18 18" />
    <path d="M10.58 10.58a3 3 0 0 0 4.24 4.24" />
    <path d="M9.88 5.08A9.53 9.53 0 0 1 12 5c6 0 10 7 10 7a15.7 15.7 0 0 1-2.24 3.34" />
    <path d="M6.24 6.24A15.51 15.51 0 0 0 2 12s4 7 10 7a9.74 9.74 0 0 0 3.5-.65" />
  </svg>
);
